import { inject, observer } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { Instance } from 'mobx-state-tree';
import { TLCode } from '../../common/enums';
import { DutyExchangeItem } from '../../store/models/DutyExchangeItem';
import { Button, Colors, Intent, Spinner, Tooltip, Label } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import moment from 'moment';
import { getFirstMonday } from '../../common/utils';

import { saveAs } from 'file-saver';

import { renderGridDateTimeWithDetails, renderGridDateWithDay } from '../../store/utils';
import { DutyType } from '../../store/models/DutyType';
import { AuthenticationStore, SystemStatusStore } from '../../store';
import { SystemStatus } from '../../store/models/SystemStatus';
import {
  DataGrid,
  Column,
  SearchPanel,
  DataGridTypes,
  Export,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  Toolbar,
  Item,
  ColumnChooser,
  FilterPanel,
  Sorting,
} from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { Container } from '../common/styledComponents/StyledComponents';
import DateInput from '../common/input/DateInput';
import { simpleDateHourFormat } from '../../common/date/dateUtils';

interface ExchangeHistoryTabProps extends WithTranslateProps {
  dutyExchangeList: Array<Instance<typeof DutyExchangeItem>>;
  exchangeHistoryIsLoading: boolean;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  secretariatId?: number;
  onSecretariatIdChanged: (date) => void;
  onSearchDateChange: (p1, p2, date) => void;
}

interface ExchangeHistoryTabState {
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  searchDate: Date;
}

const ButtonContainerLeft = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'space-between',
  gap: '20px',
  marginBottom: 5,
});

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 5px;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const mapDutyTypeToTLCode = (dutyType: Instance<typeof DutyType>) => {
  switch (dutyType.type) {
    case 'DAY':
      return TLCode.EXCHANGE_HISTORY_Day;
    case 'NIGHT':
      return TLCode.EXCHANGE_HISTORY_Night;
    default:
      return TLCode.dummy;
  }
};

const LAST_DUTY_UPLOADED_SUBSYSTEM = 'LastDutyUploadedToGeowacht';

const initialStartDutyDate = () => {
  let date = new Date();
  date.setDate(date.getDate() - 1);
  return date;
};

const createInitialState = (): ExchangeHistoryTabState => ({
  searchDate: initialStartDutyDate(),
});

@inject('SystemStatusStore')
@inject('AuthenticationStore')
@observer
class ExchangeHistoryTab extends React.Component<ExchangeHistoryTabProps, ExchangeHistoryTabState> {
  state = createInitialState();

  async componentDidMount() {
    await this.getSystemStatusLastDutyUploadedToGeowacht();
  }

  async componentDidUpdate(prevProps: Readonly<ExchangeHistoryTabProps>) {
    if (this.props.secretariatId !== prevProps.secretariatId) this.props.onSecretariatIdChanged(this.state.searchDate);
  }

  getLastGeowachtUploadDate = (): Date | null => {
    if (!this.state.systemStatus) return null;
    const entry = this.state.systemStatus.find((s) => s.subSystem === LAST_DUTY_UPLOADED_SUBSYSTEM);
    if (!entry) return null;
    return new Date(String(entry.lastModifiedOn));
  };

  buildGridData = () => {
    return this.props.dutyExchangeList.map((elem) => {
      const duty = elem.dutyAssignment.duty;
      const dutyDate = duty ? new Date(duty.dutyDate.date) : null;
      const dutyType = duty && duty.dutyType ? this.props.translate(mapDutyTypeToTLCode(duty.dutyType)) : '';
      return {
        dutyExchangeItemId: elem.dutyExchangeItemId,
        date: new Date(elem.createdOn),
        sender: elem.sender.apbNumber + ' - ' + elem.sender.name,
        recipient: elem.recipient.apbNumber + ' - ' + elem.recipient.name,
        dutyDate,
        dutyType,
        hasBeenUploaded: elem.dutyAssignment.exportedOn != null,
        requestedBy: elem.requestorName,
      };
    });
  };

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'BilanBlackpoints.xlsx');
      });
    });
  };

  async getSystemStatusLastDutyUploadedToGeowacht() {
    const criteria: any = {
      subSystem: LAST_DUTY_UPLOADED_SUBSYSTEM,
    };
    if (!this.props.SystemStatusStore) return;
    const systemStatus = await this.props.SystemStatusStore.search(criteria);
    this.setState({ systemStatus });
  }

  searchDateChange = async (date) => {
    this.setState({ searchDate: date });
    const authStore = this.props.AuthenticationStore;
    if (!authStore) return;
    this.props.onSearchDateChange(
      date,
      authStore.pharmacyId,
      authStore.isSuperAdmin ? undefined : this.props.secretariatId,
    );
  };

  setS1Date = () => {
    const firstMonday = getFirstMonday(this.state.searchDate.getFullYear(), 0);
    this.searchDateChange(firstMonday);
  };

  setS2Date = () => {
    const firstMonday = getFirstMonday(this.state.searchDate.getFullYear(), 6);
    this.searchDateChange(firstMonday);
  };

  render() {
    const { exchangeHistoryIsLoading } = this.props;
    const { translate } = this.props;

    return (
      <Container>
        {exchangeHistoryIsLoading ? (
          <SpinnerContainer>
            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
          </SpinnerContainer>
        ) : (
          <>
            <DivLine>
              <ButtonContainerLeft>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Label style={{ marginRight: '10px' }}>{translate(TLCode.EXCHANGE_HISTORY_StartDate)}</Label>
                  <Tooltip content={translate(TLCode.GENERAL_PreviousDay)} hoverOpenDelay={700}>
                    <Button
                      icon={IconNames.CHEVRON_LEFT}
                      small={true}
                      style={{ height: '30px', marginRight: '5px' }}
                      onClick={() => {
                        const date = moment(this.state.searchDate)
                          .startOf('day')
                          .subtract(1, 'd');
                        this.searchDateChange(date.toDate());
                      }}
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.GENERAL_Bankholiday)} hoverOpenDelay={700}>
                    <DateInput
                      value={this.state.searchDate}
                      canClearSelection={false}
                      onChange={(value) => {
                        this.searchDateChange(value);
                      }}
                      formatDate={(date, locale) =>
                        date
                          ? date.toLocaleString(locale, {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                            })
                          : ''
                      }
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.GENERAL_NextDay)} hoverOpenDelay={700}>
                    <Button
                      icon={IconNames.CHEVRON_RIGHT}
                      small={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={() => {
                        const date = moment(this.state.searchDate)
                          .startOf('day')
                          .add(1, 'd');
                        this.searchDateChange(date.toDate());
                      }}
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.GENERAL_Today)} hoverOpenDelay={700}>
                    <Button
                      icon={IconNames.DOUBLE_CHEVRON_DOWN}
                      small={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={() => {
                        const date = moment(new Date()).startOf('day');
                        this.searchDateChange(date.toDate());
                      }}
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.GENERAL_NextYear)} hoverOpenDelay={700}>
                    <Button
                      icon={IconNames.DOUBLE_CHEVRON_RIGHT}
                      small={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={() => {
                        const date = moment(this.state.searchDate)
                          .startOf('day')
                          .add(1, 'y');
                        date.date(1);
                        date.month(0);
                        this.searchDateChange(date.toDate());
                      }}
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.DUTYMAP_S1)} hoverOpenDelay={700}>
                    <Button
                      text={translate(TLCode.ROSTER_S1)}
                      small={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={this.setS1Date}
                    />
                  </Tooltip>
                  <Tooltip content={translate(TLCode.DUTYMAP_S2)} hoverOpenDelay={700}>
                    <Button
                      text={translate(TLCode.ROSTER_S2)}
                      small={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={this.setS2Date}
                    />
                  </Tooltip>
                  <Label style={{ marginLeft: '10px' }}>
                    {translate(TLCode.EXCHANGE_HISTORY_LastGeoWachtUpload)} :{' '}
                    {moment(this.getLastGeowachtUploadDate()).format(simpleDateHourFormat)}
                  </Label>
                </div>
              </ButtonContainerLeft>
            </DivLine>
            <DataGrid
              dataSource={this.buildGridData()}
              onRowPrepared={(r) => {
                if (r.rowType === 'data') {
                  if (r.data.hasBeenUploaded) {
                    r.rowElement.style.backgroundColor = Colors.LIGHT_GRAY1;
                  } else {
                    r.rowElement.style.backgroundColor = Colors.WHITE;
                  }
                }
              }}
              showBorders={true}
              onExporting={this.onExporting}
              allowColumnReordering={true}
              rowAlternationEnabled={false}
              width="100%"
              allowColumnResizing={true}
              columnResizingMode={'widget'}
            >
              <Sorting mode="multiple" />
              <FilterRow visible={true} applyFilter={'auto'} />
              <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
              <HeaderFilter visible={true} />
              <FilterPanel visible={true} />
              <Export enabled={true} />
              <Paging defaultPageSize={20} />
              <Pager
                visible={true}
                allowedPageSizes={[5, 10, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                showNavigationButtons={true}
              />
              <ColumnChooser enabled={true} />
              <Column
                dataType="date"
                format="dd/MM/yyyy"
                cellRender={renderGridDateWithDay}
                dataField="dutyDate"
                caption={translate(TLCode.EXCHANGE_HISTORY_DutyDate)}
                width={130}
                defaultSortOrder="asc"
                sortIndex={4}
              />
              <Column
                dataType="string"
                dataField="dutyType"
                caption={''}
                width={70}
                defaultSortOrder="asc"
                sortIndex={3}
              />
              <Column
                dataType="string"
                dataField="sender"
                caption={translate(TLCode.EXCHANGE_HISTORY_Sender)}
                minWidth={300}
                defaultSortOrder="asc"
                sortIndex={2}
              />
              <Column
                dataType="string"
                dataField="recipient"
                caption={translate(TLCode.EXCHANGE_HISTORY_Recipient)}
                width={300}
              />
              <Column
                dataType="datetime"
                format="dd/MM/yyyy HH:mm:ss"
                cellRender={renderGridDateTimeWithDetails}
                dataField="date"
                caption={translate(TLCode.EXCHANGE_HISTORY_Date)}
                minWidth={150}
                defaultSortOrder="desc"
                sortIndex={1}
              />
              <Column
                dataType="string"
                dataField="requestedBy"
                caption={translate(TLCode.EXCHANGE_HISTORY_REGISTERED_BY)}
                width={300}
              />
              <Column dataType="string" dataField="hasBeenUploaded" visible={false} />
              <Toolbar>
                <Item location="before" name="searchPanel" />
                <Item location="after" name="exportButton" />
                <Item location="after" name="columnChooserButton" />
              </Toolbar>
            </DataGrid>
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(ExchangeHistoryTab);
